/**
 * static/src/analytics/events.js
 *
 * GA4 conversion / key events for leadranks.io.
 *
 * Why this module exists (2026-09-17)
 * -----------------------------------
 * Until now the app sent exactly ONE GA4 event: `page_view`, emitted by the
 * router's afterEach (see static/src/analytics/ga4.js's page-view note). That
 * meant GA4 could show *traffic* but had nothing to count as a conversion --
 * the dashboard's "Qualified leads / Converted leads" tiles were structurally
 * pinned at 0, because no `sign_up` / `generate_lead` / `begin_checkout` /
 * `purchase` event was ever fired anywhere, frontend or backend (verified by
 * grepping both). The launch-day GA4 review found the zeros were not a data
 * delay, they were a missing instrument.
 *
 * Event contract (mark these as Key events in GA4 → Admin → Events)
 * ----------------------------------------------------------------
 *   sign_up        — account registration completed
 *   generate_lead  — a site owner actually got a finished audit back
 *                    (the real micro-conversion; see note below)
 *   audit_started  — an audit was successfully submitted (top of funnel)
 *   audit_completed— the pipeline finished an audit (fires with generate_lead,
 *                    kept separate so the funnel can be sliced either way)
 *   begin_checkout — a Stripe Checkout session was created and we're handing
 *                    the browser off to Stripe
 *   purchase       — the user came back from Stripe on a `*=success` URL
 *
 * On `generate_lead` vs `audit_started`: submitting a URL costs the visitor
 * nothing and can be a curiosity click; a *completed* audit is the point at
 * which they got something of value and the funnel is realistically still
 * alive. So `generate_lead` fires on completion, not on submit -- otherwise
 * "qualified leads" would count drive-by submissions and the number would be
 * worthless. `audit_started` is the honest name for the submit itself.
 *
 * Design rules
 * ------------
 *  - Analytics must NEVER break the app: every entry point swallows its own
 *    errors and no-ops when gtag isn't ready (adblockers, CSP quirks, SSR).
 *  - No PII in event params. Do NOT pass emails, names, or full URLs with
 *    query strings. Domain-only is fine where useful.
 *  - `*Once` guards exist because several of these fire on page load of a
 *    redirect target (Stripe's success_url, the audit-complete redirect) and
 *    a refresh would otherwise double-count a real conversion. Guards use
 *    sessionStorage by default (per browser tab session) and localStorage for
 *    per-entity keys like an analysis id, which is naturally unique per audit.
 *
 * Money (2026-09-17, follow-up)
 * -----------------------------
 * `purchase` carries `currency: 'USD'` (leadranks.io bills in USD) plus a
 * `value` whenever the amount is known, so GA4's Monetization reports show
 * real revenue instead of a bare count. `value`/`items` are OMITTED -- never
 * sent as 0 -- when there is no fixed amount (free/custom tiers), because a
 * zero-value purchase records as real zero revenue.
 *
 * Dedupe: Stripe's success_urls append `session_id={CHECKOUT_SESSION_ID}`
 * (see stripe_endpoints.py), and a purchase is guarded on that session id in
 * localStorage. That survives what a per-tab-session guard could not: a
 * refresh days later, a bookmark, or reopening the success URL in a new
 * browser session would otherwise re-count the same payment. Without an id we
 * fall back to the old one-per-tab-session behaviour.
 */

/** Storage key prefix, so these are trivially auditable/clearable. */
const ONCE_PREFIX = 'lr_ga_once:';

/**
 * Send a GA4 event. Returns true if it was handed to gtag.
 * Never throws — a failed event must not affect the caller.
 */
export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined' || !name) return false;
  if (typeof window.gtag !== 'function') return false;
  try {
    window.gtag('event', name, params);
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[ga4] event failed:', name, e && e.message ? e.message : e);
    return false;
  }
}

/**
 * Send an event at most once per storage scope.
 *
 * @param {string} key      dedupe key (namespaced internally)
 * @param {string} name     GA4 event name
 * @param {object} params   GA4 event params
 * @param {'session'|'local'} scope  sessionStorage (default) or localStorage
 */
export function trackEventOnce(key, name, params = {}, scope = 'session') {
  if (typeof window === 'undefined' || !key) return false;
  const storage = scope === 'local' ? window.localStorage : window.sessionStorage;
  const fullKey = ONCE_PREFIX + key;
  try {
    if (storage.getItem(fullKey)) return false;
    storage.setItem(fullKey, '1');
  } catch (e) {
    // Storage disabled (private mode / blocked cookies): fall through and
    // send the event rather than silently dropping the conversion. A rare
    // double-count is a better failure mode than zero counts.
  }
  return trackEvent(name, params);
}

/** Account registration completed. Fired from RegisterPage.vue on success. */
export function trackSignUp({ method = 'email', tier = 'free' } = {}) {
  return trackEventOnce('sign_up', 'sign_up', { method, tier });
}

/** A Stripe Checkout session was created; the browser is about to leave. */
export function trackBeginCheckout({ tier = null, billing = 'monthly', item = 'subscription' } = {}) {
  return trackEvent('begin_checkout', { item, tier, billing });
}

/**
 * Pull the Stripe Checkout session id off a success URL's query string.
 *
 * Returns null for anything that isn't a real session id -- notably the
 * literal `{CHECKOUT_SESSION_ID}` template, which reaches the browser
 * unsubstituted if Stripe ever fails to expand it. Sending that as a
 * transaction id would be worse than sending none.
 */
export function transactionIdFrom(raw) {
  const id = raw === null || raw === undefined ? '' : String(raw).trim();
  return /^cs_[A-Za-z0-9_]{6,}$/.test(id) ? id : null;
}

/**
 * The visitor returned from Stripe on a success URL.
 *
 * @param {string|null} tier      tier key (Stripe-side names accepted)
 * @param {string}      item      what was bought: subscription | lead_credits | audit_credits
 * @param {string}      billing   monthly | yearly
 * @param {number|null} value     amount billed, in USD; omit when unknown
 * @param {string|null} transactionId  Stripe Checkout session id, when present
 */
export function trackPurchase({
  tier = null,
  item = 'subscription',
  billing = 'monthly',
  value = null,
  transactionId = null,
} = {}) {
  const params = { item, tier, billing, currency: 'USD' };

  // Only attach money when we actually know it. A missing `value` leaves the
  // count intact and the revenue unknown, which is honest; a 0 would read as a
  // real zero-revenue sale.
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    params.value = value;
    params.items = [{
      item_id: `${item}_${tier || 'unknown'}`,
      item_name: item,
      price: value,
      quantity: 1,
    }];
  }

  const txn = transactionIdFrom(transactionId);
  if (txn) {
    params.transaction_id = txn;
    // localStorage: the same Stripe payment must never count twice, however
    // long after the fact the success URL is re-opened.
    return trackEventOnce(`purchase:txn:${txn}`, 'purchase', params, 'local');
  }

  // No session id (older link, or Stripe didn't expand the template): fall
  // back to one purchase per tab session per item.
  return trackEventOnce(`purchase:${item}:${tier || 'unknown'}`, 'purchase', params);
}

/** Audit submitted successfully (top of funnel). */
export function trackAuditStarted({ domain = null, mode = null } = {}) {
  // Keyed by domain+mode: a visitor re-running the same audit in one session
  // is one funnel entry, not three.
  return trackEventOnce(`audit_started:${domain || 'unknown'}:${mode || 'default'}`, 'audit_started', {
    domain,
    mode,
  });
}

/**
 * The pipeline finished an audit — the engagement's real conversion point.
 * Keyed on the analysis id (naturally unique), stored in localStorage so the
 * guard survives the processing → report navigation.
 */
export function trackAuditCompleted({ analysis_id = null } = {}) {
  if (analysis_id) {
    return trackEventOnce(`audit_completed:${analysis_id}`, 'audit_completed', { analysis_id }, 'local');
  }
  return trackEvent('audit_completed', { analysis_id });
}

/** The finished-audit lead. See the module docstring on why this is here. */
export function trackGenerateLead({ lead_type = 'audit', analysis_id = null, domain = null } = {}) {
  if (analysis_id) {
    return trackEventOnce(`generate_lead:${analysis_id}`, 'generate_lead', {
      lead_type,
      analysis_id,
      domain,
    }, 'local');
  }
  return trackEvent('generate_lead', { lead_type, domain });
}
